'use client';

import { useState } from 'react';
import { Flame, Plus } from 'lucide-react';
import { Candle } from '@/lib/types';

interface CandleWallProps {
  candles: Candle[];
  memorialSlug?: string;
}

export function CandleWall({ candles: initialCandles, memorialSlug }: CandleWallProps) {
  const [candles, setCandles] = useState(initialCandles);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memorialSlug) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/memorials/${memorialSlug}/candles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || 'Anonym',
          description: message.trim() || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to light candle');
      }

      // Add the new candle to the list (at the beginning)
      setCandles([
        {
          id: data.candle.id,
          name: data.candle.name,
          description: data.candle.description,
          email: null,
          image: null,
          createdAt: data.candle.createdAt,
        },
        ...candles,
      ]);

      setName('');
      setMessage('');
      setShowForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 bg-gradient-to-b from-memorial-primary/95 to-memorial-primary text-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Flame className="w-7 h-7 text-amber-400 candle-flame" />
            <h2 className="font-serif text-2xl md:text-3xl">
              Kerzen des Gedenkens
            </h2>
          </div>
          <p className="text-white/70">
            {candles.length} {candles.length === 1 ? 'Kerze wurde' : 'Kerzen wurden'} angezündet
          </p>
        </div>

        {/* Candle grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4 mb-8">
          {candles.map((candle) => (
            <div
              key={candle.id}
              className="flex flex-col items-center text-center group"
              title={candle.description || undefined}
            >
              {/* Candle */}
              <div className="relative mb-2">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-amber-400/30 rounded-full blur-xl scale-150 candle-flame" />
                {/* Flame */}
                <div className="relative">
                  <Flame className="w-10 h-10 text-amber-400 candle-flame drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]" />
                </div>
              </div>
              {/* Name */}
              <span className="text-xs text-white/80 truncate max-w-full px-1">
                {candle.name || 'Anonym'}
              </span>
              {/* Message tooltip on hover */}
              {candle.description && (
                <div className="hidden group-hover:block absolute -bottom-8 left-1/2 -translate-x-1/2 bg-white text-memorial-text-primary text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10">
                  {candle.description}
                </div>
              )}
            </div>
          ))}

          {/* Light candle button */}
          {memorialSlug && (
            <button
              onClick={() => setShowForm(true)}
              className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-white/30 rounded-lg hover:border-white/50 hover:bg-white/5 transition-colors"
            >
              <Plus className="w-8 h-8 text-white/50 mb-1" />
              <span className="text-xs text-white/50">Kerze anzünden</span>
            </button>
          )}
        </div>

        {/* Light candle form */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white text-memorial-text-primary rounded-xl p-6 max-w-md w-full shadow-xl animate-fade-in">
              <div className="flex items-center justify-center gap-2 mb-6">
                <Flame className="w-6 h-6 text-amber-500" />
                <h3 className="font-serif text-xl text-memorial-primary">
                  Kerze anzünden
                </h3>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="candle-name" className="block text-sm font-medium mb-1">
                    Ihr Name (optional)
                  </label>
                  <input
                    type="text"
                    id="candle-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="z.B. Familie Schmidt"
                    className="w-full px-4 py-2 border border-memorial-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/50"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="candle-message" className="block text-sm font-medium mb-1">
                    Kurze Nachricht (optional)
                  </label>
                  <input
                    type="text"
                    id="candle-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="z.B. In stillem Gedenken"
                    maxLength={100}
                    className="w-full px-4 py-2 border border-memorial-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/50"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setError(null);
                    }}
                    className="flex-1 px-4 py-2 border border-memorial-muted rounded-lg text-memorial-text-secondary hover:bg-memorial-muted/50 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Wird angezündet...' : 'Kerze anzünden'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
